import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import api from "@/lib/api";
import { fetchSettings, logoSrc } from "@/lib/settings";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Menu, X } from "lucide-react";

export default function PublicPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [navPages, setNavPages] = useState([]);
  const [cms, setCms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Renka";
    Promise.all([
      api.get(`/site/pages/${slug}`).then((r) => r.data).catch(() => null),
      api.get("/site/pages/navigation").then((r) => r.data).catch(() => []),
      fetchSettings().catch(() => null),
    ]).then(([p, nav, settings]) => {
      if (!p) {
        setError("Sayfa bulunamadı");
      } else {
        setPage(p);
        document.title = `${p.seo_title || p.title} - Renka`;
      }
      setNavPages(nav || []);
      setCms(settings);
      setLoading(false);
    });
  }, [slug]);

  const logoUrl = logoSrc(cms);
  const headerPages = navPages.filter((p) => p.show_in_header);
  const footerPages = navPages.filter((p) => p.show_in_footer);
  const loginLabel = cms?.nav_login || "Giriş Yap";
  const footerLinksTitle = cms?.footer_links_title || "Hızlı Bağlantılar";
  const footerTagline = cms?.footer_tagline || "Özel eğitimde dijital dönüşüm.";
  const footerRights = cms?.footer_rights || "Tüm hakları saklıdır.";

  const goLogin = () => navigate("/login");

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-white px-4">
        <p className="text-lg font-semibold text-slate-800">{error || "Sayfa bulunamadı"}</p>
        <Button onClick={() => navigate("/")} className="mt-4 rounded-full bg-violet-600">Ana Sayfaya Dön</Button>
      </div>
    );
  }

  const html = DOMPurify.sanitize(page.content_html || "", {
    ADD_TAGS: ["iframe"],
    ADD_ATTR: ["target", "rel", "class", "style"],
  });

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="public-page">
      <header className="sticky top-0 z-40 border-b border-slate-100/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Link to="/"><Logo className="h-8 w-auto object-contain sm:h-9" alt="RENKA" /></Link>
          <nav className="hidden items-center gap-6 md:flex">
            <Link to="/" className="text-sm font-medium text-slate-600 hover:text-violet-600">Anasayfa</Link>
            {headerPages.map((p) => (
              <Link key={p.id} to={`/sayfa/${p.slug}`} className={`text-sm font-medium transition-colors ${p.slug === slug ? "text-violet-600" : "text-slate-600 hover:text-violet-600"}`}>
                {p.title}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button onClick={goLogin} className="hidden h-9 rounded-full bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700 sm:inline-flex">{loginLabel}</Button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 text-slate-600 md:hidden" aria-label="Menu">{menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
          </div>
        </div>
        {menuOpen && (
          <div className="border-t border-slate-100 bg-white px-4 py-3 md:hidden">
            <Link to="/" className="block py-2 text-sm font-medium text-slate-600" onClick={() => setMenuOpen(false)}>Anasayfa</Link>
            {headerPages.map((p) => (
              <Link key={p.id} to={`/sayfa/${p.slug}`} className="block py-2 text-sm font-medium text-slate-600" onClick={() => setMenuOpen(false)}>{p.title}</Link>
            ))}
          </div>
        )}
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <button onClick={() => navigate(-1)} className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-4 w-4" /> Geri
        </button>
        <article>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{page.title}</h1>
          <div
            className="page-content mt-8"
            dangerouslySetInnerHTML={{ __html: html }}
            data-testid="public-page-content"
          />
        </article>
      </main>

      <footer className="mt-16 bg-slate-950 py-12 text-slate-300">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div>
              <Logo className="h-9 w-auto object-contain" alt="RENKA" />
              <p className="mt-3 max-w-xs text-sm text-slate-400">{footerTagline}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{footerLinksTitle}</p>
              <nav className="mt-3 flex flex-col gap-2">
                <Link to="/" className="text-sm text-slate-400 hover:text-white">Anasayfa</Link>
                {footerPages.map((p) => (
                  <Link key={p.id} to={`/sayfa/${p.slug}`} className="text-sm text-slate-400 hover:text-white">{p.title}</Link>
                ))}
              </nav>
            </div>
          </div>
          <p className="mt-10 border-t border-white/10 pt-6 text-center text-xs text-slate-500">© {new Date().getFullYear()} Renka. {footerRights}</p>
        </div>
      </footer>
    </div>
  );
}
